<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useI18n } from '@/i18n'
import { vizMeta, sourceMeta, wl } from '@/config/widgetSources'

const props = defineProps({
  widget:    { type: Object,  required: true },
  editMode:  { type: Boolean, default: false },
  // [{id, title}] aller Sections — fürs Verschieben im Configurator
  sections:  { type: Array,   default: () => [] },
  sectionId: { type: String,  default: null },
})

const emit = defineEmits(['remove', 'update', 'move-to', 'clone'])

const { t, activeLang } = useI18n()

const VIZ_COMPONENTS = {
  kpi:           defineAsyncComponent(() => import('@/components/dashboard/generic/VizKpi.vue')),
  summary:       defineAsyncComponent(() => import('@/components/dashboard/generic/VizSummary.vue')),
  table:         defineAsyncComponent(() => import('@/components/dashboard/generic/VizTable.vue')),
  donut:         defineAsyncComponent(() => import('@/components/dashboard/generic/VizDonut.vue')),
  'donut-table': defineAsyncComponent(() => import('@/components/dashboard/generic/VizDonutTable.vue')),
  line:          defineAsyncComponent(() => import('@/components/dashboard/generic/VizLine.vue')),
  map:           defineAsyncComponent(() => import('@/components/dashboard/generic/VizMap.vue')),
  feed:          defineAsyncComponent(() => import('@/components/dashboard/generic/VizFeed.vue')),
  info:          defineAsyncComponent(() => import('@/components/dashboard/generic/VizInfo.vue')),
}

const Configurator = defineAsyncComponent(() => import('@/components/dashboard/WidgetConfigurator.vue'))

const configuring = ref(false)
const isUnconfigured = computed(() => !props.widget.viz)
const showConfigurator = computed(() => isUnconfigured.value || configuring.value)

const meta = computed(() => sourceMeta(props.widget.source))

const widgetTitle = computed(() =>
  props.widget.config?.title
  || wl(meta.value?.label, activeLang.value)
  || wl(vizMeta(props.widget.viz)?.label, activeLang.value)
  || t('dashboard.newWidget')
)

const widgetIcon = computed(() => vizMeta(props.widget.viz)?.icon ?? 'pi pi-th-large')

const vizComponent = computed(() => VIZ_COMPONENTS[props.widget.viz] ?? null)

// ── Configurator ──────────────────────────────────────────────────────────────
function onConfiguratorApply({ viz, source, config, sectionId }) {
  emit('update', { viz, source, config })
  if (sectionId && sectionId !== props.sectionId) {
    emit('move-to', sectionId)
  }
  configuring.value = false
}

function onConfiguratorCancel() {
  if (isUnconfigured.value) {
    emit('remove', props.widget.i)
  }
  configuring.value = false
}
</script>

<template>
  <div class="hw-card" :class="{ 'is-editing': editMode }">

    <!-- ── Header ──────────────────────────────────────────────────── -->
    <div class="hw-card-header">
      <div v-if="editMode" class="dw-drag-handle">
        <i class="pi pi-arrows-alt" />
      </div>
      <i :class="[widgetIcon, 'hw-card-icon']" />
      <span class="hw-card-title">{{ widgetTitle }}</span>

      <!-- Edit-Aktionen -->
      <template v-if="editMode">
        <button
          v-if="!isUnconfigured"
          class="dw-icon-btn"
          :title="t('dashboard.configureWidget')"
          @click.stop="configuring = !configuring"
        >
          <i class="pi pi-cog" />
        </button>
        <button
          v-if="!isUnconfigured"
          class="dw-icon-btn"
          :title="t('dashboard.cloneWidget')"
          @click.stop="emit('clone', widget.i)"
        >
          <i class="pi pi-clone" />
        </button>
        <button
          class="dw-icon-btn dw-icon-btn--delete"
          :title="t('dashboard.removeWidget')"
          @click.stop="emit('remove', widget.i)"
        >
          <i class="pi pi-times" />
        </button>
      </template>
    </div>

    <!-- ── Content ─────────────────────────────────────────────────── -->
    <div class="dw-body">
      <Configurator
        v-if="showConfigurator"
        :widget="isUnconfigured ? null : widget"
        :sections="sections"
        :current-section-id="sectionId"
        @apply="onConfiguratorApply"
        @cancel="onConfiguratorCancel"
      />
      <component :is="vizComponent" v-else-if="vizComponent" :widget="widget" />
      <div v-else class="dw-unknown">
        <i class="pi pi-question-circle" />
        <span>{{ widget.viz }}</span>
      </div>
    </div>

  </div>
</template>

<style scoped>
/* vue-grid-layout setzt pixel-height auf grid-items; hw-card muss 100% füllen */
.hw-card {
  height: 100%;
}

.is-editing {
  border-color: var(--p-primary-color) !important;
  border-style: dashed !important;
}

.dw-drag-handle {
  cursor: grab;
  color: var(--p-text-color-secondary);
  font-size: .8rem;
  opacity: .45;
  flex-shrink: 0;
  padding: 4px;
  border-radius: 4px;
  transition: opacity .12s;
  order: -1;
}
.dw-drag-handle:hover { opacity: 1; }

/* ── Icon-Buttons ── */
.dw-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-size: .65rem;
  color: var(--p-text-color-secondary);
  transition: background .12s, color .12s;
  flex-shrink: 0;
}
.dw-icon-btn:hover {
  background: var(--p-surface-200, rgba(0,0,0,.08));
  color: var(--p-text-color);
}
.dw-icon-btn--delete:hover {
  background: var(--p-red-100, #fee2e2);
  color: var(--p-red-600, #dc2626);
}

.dw-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.dw-unknown {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 8px;
  color: var(--p-text-color-secondary);
  font-size: .85rem;
}

:global(.my-app-dark) .dw-icon-btn:hover {
  background: var(--p-surface-700, rgba(255,255,255,.08));
}
:global(.my-app-dark) .dw-icon-btn--delete:hover {
  background: rgba(220,38,38,.15);
  color: #f87171;
}
</style>
